import React, { useEffect, useState, useRef } from "react";
import { useSwipeable } from "react-swipeable";
import "./CustomSlider.css";

export default function CustomSlider({
  slides,
  enableDrag = true,
  onNavigate,
  isSidebarOpen,
  screenSize,
  screenHeight,
}) {
  const [activeIndex, setActiveIndex] = useState(0);
  const [animating, setAnimating] = useState(false);
  const [dragging, setDragging] = useState(false);
  const [dragStart, setDragStart] = useState(0);
  const [dragOffset, setDragOffset] = useState(0);
  const autoSlideDelay = 6000;
  const autoSlideTimeout = useRef(null);

  useEffect(() => {
    if (slides.length > 0) {
      const startAutoSlide = () => {
        autoSlideTimeout.current = setTimeout(() => {
          if (!animating) {
            setAnimating(true);
            setActiveIndex((prevIndex) => (prevIndex + 1) % slides.length);
          }
        }, autoSlideDelay);
      };
      startAutoSlide();
      return () => clearTimeout(autoSlideTimeout.current);
    }
  }, [activeIndex, slides.length, animating]);

  const nextSlide = () => {
    if (animating) return;
    setAnimating(true);
    setActiveIndex((prevIndex) => (prevIndex + 1) % slides.length);
  };

  const prevSlide = () => {
    if (animating) return;
    setAnimating(true);
    setActiveIndex(
      (prevIndex) => (prevIndex - 1 + slides.length) % slides.length
    );
  };

  useEffect(() => {
    const handleTransitionEnd = () => {
      setAnimating(false);
      if (autoSlideTimeout.current) {
        clearTimeout(autoSlideTimeout.current);
      }
      autoSlideTimeout.current = setTimeout(() => {
        if (!animating) {
          setAnimating(true);
          setActiveIndex((prevIndex) => (prevIndex + 1) % slides.length);
        }
      }, autoSlideDelay);
    };

    const sliderElement = document.querySelector(".slider");
    if (sliderElement) {
      sliderElement.addEventListener("transitionend", handleTransitionEnd);
    }

    return () => {
      if (sliderElement) {
        sliderElement.removeEventListener("transitionend", handleTransitionEnd);
      }
    };
  }, [animating, slides.length]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.keyCode === 37) {
        prevSlide();
      } else if (e.keyCode === 39) {
        nextSlide();
      }
    };

    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, []);

  const handleDotClick = (index) => {
    if (animating || index === activeIndex) return;
    setAnimating(true);
    setActiveIndex(index);
  };

  const handleDragStart = (e) => {
    if (!enableDrag) return;
    setDragging(true);
    setDragStart(e.clientX || e.touches[0].clientX);
  };

  const handleDragMove = (e) => {
    if (!dragging) return;
    const currentX =
      e.clientX || (e.touches && e.touches[0] && e.touches[0].clientX);
    if (currentX !== undefined) {
      setDragOffset(currentX - dragStart);
    }
  };

  const handleDragEnd = () => {
    if (!dragging) return;
    setDragging(false);
    if (dragOffset > 50) {
      prevSlide();
    } else if (dragOffset < -50) {
      nextSlide();
    }
    setDragOffset(0);
  };

  const handlers = useSwipeable({
    onSwipedLeft: nextSlide,
    onSwipedRight: prevSlide,
    preventDefaultTouchmoveEvent: true,
    trackMouse: true,
  });

  if (!slides || slides.length === 0) {
    return null; // or a loading spinner
  }

  const handleLinkClick = (e, url) => {
    if (animating) {
      e.preventDefault();
    } else if (onNavigate && url) {
      e.preventDefault();
      onNavigate(url);
    }
  };

  const renderPagination = () => {
    if ((isSidebarOpen && screenSize !== "full") || screenHeight === "small") {
      return <p>{`${activeIndex + 1}/${slides.length}`}</p>;
    } else {
      return slides.map((_, index) => (
        <li
          key={index}
          className={`slider-pagi__elem ${
            index === activeIndex ? "active" : ""
          }`}
          onClick={() => handleDotClick(index)}
          style={{ "--i": index + 1 }}
        ></li>
      ));
    }
  };

  return (
    <div
      className="slider-container"
      {...handlers}
      onMouseDown={handleDragStart}
      onMouseMove={handleDragMove}
      onMouseUp={handleDragEnd}
      onMouseLeave={handleDragEnd}
      onTouchStart={handleDragStart}
      onTouchMove={handleDragMove}
      onTouchEnd={handleDragEnd}
    >
      <div className="slider-control left" onClick={prevSlide}></div>
      <div className="slider-control right" onClick={nextSlide}></div>
      <ul className="slider-pagi">{renderPagination()}</ul>
      <div
        className={`slider ${animating ? "animating" : ""}`}
        style={{
          transform: `translate3d(${
            dragging
              ? -activeIndex * 100 + (dragOffset / window.innerWidth) * 800
              : -activeIndex * 100
          }%, 0, 0)`,
        }}
      >
        {slides.map((slide, index) => {
          const slideClass =
            index === activeIndex
              ? "active"
              : index === (activeIndex - 1 + slides.length) % slides.length
              ? "prev"
              : index === (activeIndex + 1) % slides.length
              ? "next"
              : "";
          return (
            <div
              key={index}
              className={`slide ${slideClass}`}
              style={{ left: `${index * 100}%` }}
            >
              <img
                src={slide.image} 
                alt={slide.title}
                className="slide__bg-img slide__bg"
                loading={index === 0 ? "eager" : "lazy"}
                style={{
                  transform: `translate3d(${activeIndex * 50}%, 0, 0)`,
                   left: `-${index * 50}%`,
                   objectFit: 'cover'
                }}
              />
              {slide.url ? (
                <a
                  href={slide.url}
                  className="single-link"
                  onClick={(e) => handleLinkClick(e, slide.url)}
                ></a>
              ) : (
                <div className="single-link"></div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
